// One session: timeline, spans, corrections with history, tokens and the raw events (metadata only).
import { useState, type ReactNode } from 'react';
import type { ActivitySpan, RawEvent, SpanEdit } from '../api/types.ts';
import { navigate } from '../lib/router.ts';
import { activeProjects, displayTitle, project as findProject, providerInfo, type RangeData } from '../model/data.ts';
import * as m from '../model/mutations.ts';
import {
  detailFigures, detailLanes, detailSpanEnd, sessionDetail, spanPieces, totalTokens, usageIsEmpty, type DetailFigures, type SessionDetail, type UsageSummary,
} from '../model/session.ts';
import { Banner, Confirm, EmptyState, Figure, Loading, Menu, MenuDivider, MenuItem, MenuLabel, ScreenHeader, SectionHeader, TextInput } from '../components/common.tsx';
import { AssignProjectMenu } from '../components/pickers.tsx';
import { useFmt, useNow, useSession, useStore } from '../store/hooks.tsx';
import { Button, Monogram, ProjectChip, StatusPill, type PillStateName } from '../ui/ds.tsx';
import { Icon } from '../ui/Icon.tsx';
import { ActivityTimeline } from '../ui/timeline.tsx';

export function SessionDetailScreen({ id }: { id: string }) {
  const store = useStore();
  const t = store.t;
  const now = useNow(1000);
  const loaded = useSession(id);
  const back = <Button className="back" variant="plain" size="sm" icon="chevronLeft" onClick={() => navigate({ name: 'sessions' })}>{t('Sessions')}</Button>;
  if (loaded.data === undefined) {
    return <div className="screen"><ScreenHeader back={back} title={t('Session')} />{loaded.error ? <Banner tone="danger">{loaded.error}</Banner> : <Loading label={t('Lädt …')} />}</div>;
  }
  const detail = loaded.data ? sessionDetail(loaded.data, id) : null;
  if (!detail || !loaded.data) {
    return (
      <div className="screen">
        <ScreenHeader back={back} title={t('Session')} />
        <EmptyState icon="terminal" title={t('Session nicht gefunden')} message={t('Sie wurde gelöscht oder gehört zu einem anderen Konto.')}>
          <Button onClick={() => navigate({ name: 'sessions' })}>{t('Zu den Sessions')}</Button>
        </EmptyState>
      </div>
    );
  }
  return <Detail detail={detail} data={loaded.data} events={loaded.events ?? []} now={now} back={back} />;
}

function Detail({ detail: d, data, events, now, back }: { detail: SessionDetail; data: RangeData; events: RawEvent[]; now: number; back: ReactNode }) {
  const store = useStore();
  const t = store.t;
  const fmt = useFmt();
  const r = d.record;
  const figures = detailFigures(d, now);
  const [renaming, setRenaming] = useState(false);
  const [title, setTitle] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);
  const provider = providerInfo(r.provider);
  const subtitle = [provider.name, fmt.started(r.startedAt, now),
    r.method === 'logs' ? t('über Session-Logs') : r.method === 'cli' ? 'CLI' : r.method === 'api' ? t('Server') : 'Hooks',
    ...(r.coverage === 'partial' ? [t('Teilabdeckung')] : []), ...(d.children.length > 0 ? [t('%lld Subagents', d.children.length)] : [])].join(' · ');
  const rename = () => {
    void store.apply(m.renameSession(r, title, store.clock()));
    setRenaming(false);
  };
  const project = findProject(store.ws, r.projectId);
  return (
    <div className="screen">
      <div className="detail-head">
        {back}
        <div className="detail-head__row">
          <Monogram text={provider.mono} provider={provider.id} />
          <div className="detail-head__titles">
            {renaming ? (
              <form className="rename" onSubmit={(e) => { e.preventDefault(); rename(); }}>
                <TextInput autoFocus value={title} placeholder={displayTitle(r)} aria-label={t('Titel')} onChange={(e) => setTitle(e.target.value)} />
                <Button type="submit" variant="primary" size="sm">{t('Sichern')}</Button>
                <Button variant="plain" size="sm" onClick={() => setRenaming(false)}>{t('Abbrechen')}</Button>
              </form>
            ) : <h1>{displayTitle(r)}</h1>}
            <p>{subtitle}</p>
          </div>
          <StatusPill state={figures.state} />
          <Menu label={t('Mehr')} align="end" trigger={(props) => <Button variant="plain" iconOnly icon="more" aria-label={t('Mehr')} {...props} />}>
            {(menu) => (
              <>
                <MenuItem onSelect={() => { menu.close(); setTitle(r.titleSource === 'manual' ? r.title ?? '' : ''); setRenaming(true); }}>{t('Umbenennen …')}</MenuItem>
                {r.titleSource === 'manual' ? <MenuItem onSelect={() => { menu.close(); void store.apply(m.renameSession(r, '', store.clock())); }}>{t('Standardtitel verwenden')}</MenuItem> : null}
                <MenuDivider />
                <MenuLabel>{t('Projekt zuordnen')}</MenuLabel>
                {activeProjects(store.ws).map((p) => (
                  <MenuItem key={p.id} checked={p.id === r.projectId} onSelect={() => { menu.close(); void store.apply(m.assignSession(store.ws, r, p.id, false, data.sessions, store.clock())); }}>
                    <span className="menu__proj"><span className="tl-dot" style={{ background: `var(--project-${p.colorSlot})` }} />{p.name}</span>
                  </MenuItem>
                ))}
                <MenuDivider />
                <MenuItem danger onSelect={() => { menu.close(); setConfirmDelete(true); }}>{t('Session löschen …')}</MenuItem>
              </>
            )}
          </Menu>
        </div>
      </div>

      <section className="tl-card facts">
        <div className="facts__row">
          <div className="fact">
            <span className="fact__label">{t('Projekt')}</span>
            {project ? <ProjectChip name={project.name} color={project.colorSlot} /> : <AssignProjectMenu session={r} loaded={data.sessions} />}
          </div>
          <Figure label={t('Gezählt')} value={fmt.duration(figures.countedMs)} strong />
          <div className="fact"><span className="fact__label">{t('Gewartet · nicht gezählt')}</span><span className="figure__value waiting-value">{fmt.duration(figures.waitingMs)}</span></div>
          {figures.discardedMs > 0 || figures.movedMs > 0 ? (
            <div className="fact"><span className="fact__label">{t('Korrigiert')}</span><span className="note">{[
              ...(figures.movedMs > 0 ? [t('%@ umgehängt', fmt.duration(figures.movedMs))] : []),
              ...(figures.discardedMs > 0 ? [t('%@ verworfen', fmt.duration(figures.discardedMs))] : []),
            ].join(' · ')}</span></div>
          ) : null}
          {r.model ? <div className="fact"><span className="fact__label">{t('Modell')}</span><span className="tl-mono mono-ink">{r.model}</span></div> : null}
        </div>
        <div className="facts__where">
          {r.repoName ? <span className="tl-mono"><Icon name="folder" size={13} />{r.repoName}</span> : null}
          {r.gitBranch ? <span className="tl-mono"><Icon name="branch" size={13} />{r.gitBranch}</span> : null}
          {r.gitRemote ? <span className="tl-mono"><Icon name="network" size={13} />{r.gitRemote}</span> : null}
        </div>
      </section>

      <section className="tl-card panel">
        <SectionHeader title={t('Timeline')} caption={t('Grün zählt · schraffiert wartet, nicht gezählt')} />
        <ActivityTimeline {...figures.timeline} now={figures.isOpen ? now : null} tickEveryHours={figures.tickHours} label={t('Timeline der Session')}
          lanes={detailLanes(d, figures, now, { waitingInput: t('Wartet auf Eingabe'), waitingPermission: t('Wartet auf Freigabe'), subagent: t('Subagent') })
            .map((l) => ({ ...l, source: 'agent' as const }))} />
      </section>

      <div className="stack">
        <SectionHeader title={t('Spans')} caption={t('%lld Abschnitte · jede Minute führt zu ihren Events', d.spans.length)} />
        <div className="tl-card table">
          {d.spans.map((span) => <SpanRow key={span.id} span={span} detail={d} figures={figures} now={now} />)}
        </div>
      </div>

      <Corrections edits={d.edits} now={now} />
      {!usageIsEmpty(d.usage) ? <Usage usage={d.usage} countedMs={figures.countedMs} /> : null}
      <Events events={events} now={now} />

      {confirmDelete ? (
        <Confirm title={t('Session löschen?')} danger confirm={t('Löschen')} cancel={t('Abbrechen')} onClose={() => setConfirmDelete(false)}
          message={t('Die Session, ihre Events, Spans, Korrekturen und Token-Zahlen werden auf allen Geräten gelöscht. Abgerechnete Zeit fällt damit weg.')}
          onConfirm={() => { void store.apply(m.deleteSession(r, data.sessions)); navigate({ name: 'sessions' }); }} />
      ) : null}
    </div>
  );
}

function spanPill(span: ActivitySpan): PillStateName {
  switch (span.state) {
    case 'working': return 'working';
    case 'waiting_input': return 'waiting';
    case 'waiting_permission': return 'permission';
    default: return 'idle';
  }
}

function SpanRow({ span, detail, figures, now }: { span: ActivitySpan; detail: SessionDetail; figures: DetailFigures; now: number }) {
  const store = useStore();
  const t = store.t;
  const fmt = useFmt();
  const end = detailSpanEnd(detail, span, now);
  const working = span.state === 'working';
  const parts = working ? spanPieces(figures, span, end) : [];
  const counted = parts.reduce((n, p) => n + (Math.min(p.end, end) - Math.max(p.start, span.startAt)), 0);
  const label = span.state === 'working' ? t('Arbeitet · gezählt') : span.state === 'waiting_input' ? t('Wartet · nicht gezählt')
    : span.state === 'waiting_permission' ? t('Freigabe · nicht gezählt') : t('Inaktiv');
  const projects = [...new Set(parts.map((p) => p.project ?? ''))].sort();
  const request = (mode: 'reassign' | 'split' | 'discard') => store.openSheet({ kind: 'spanEdit', sessionId: detail.record.id, range: { start: span.startAt, end }, mode });
  return (
    <div className="table__row span-grid">
      <span className="table__cell"><StatusPill state={spanPill(span)} label={label} size="sm" /></span>
      <span className="table__num table__num--muted span-time">{`${fmt.time(span.startAt)}–${span.endAt === null ? t('jetzt') : fmt.time(end)}`}</span>
      <span className="table__cell span-where">
        {working ? (parts.length === 0 ? <span className="note--faint">{t('verworfen')}</span> : (
          <>
            {projects.map((id) => {
              const p = findProject(store.ws, id);
              return p ? <ProjectChip key={id} name={p.name} color={p.colorSlot} plain /> : <span key="none" className="note--faint">{t('ohne Projekt')}</span>;
            })}
            {counted < end - span.startAt - 999 ? <span className="note--faint">{t('teilweise verworfen')}</span> : null}
          </>
        )) : span.state === 'idle' && span.reason === 'stale' ? <span className="note--faint">{t('Keine Events mehr: Zählung beendet')}</span> : null}
      </span>
      <span className={working && parts.length === 0 ? 'table__num struck' : working ? 'table__num' : 'table__num table__num--muted'}>{fmt.duration(end - span.startAt)}</span>
      <span className="span-actions">
        {working ? (
          <Menu label={t('Span korrigieren')} align="end" trigger={(props) => <Button variant="plain" size="sm" iconOnly icon="more" aria-label={t('Span korrigieren')} {...props} />}>
            {(menu) => (
              <>
                <MenuItem onSelect={() => { menu.close(); request('reassign'); }}>{t('Anderem Projekt zuordnen …')}</MenuItem>
                <MenuItem onSelect={() => { menu.close(); request('split'); }}>{t('Teilen …')}</MenuItem>
                <MenuDivider />
                <MenuItem onSelect={() => { menu.close(); request('discard'); }}>{t('Verwerfen …')}</MenuItem>
              </>
            )}
          </Menu>
        ) : null}
      </span>
    </div>
  );
}

function Corrections({ edits, now }: { edits: SpanEdit[]; now: number }) {
  const store = useStore();
  const t = store.t;
  const fmt = useFmt();
  return (
    <div className="stack">
      <SectionHeader title={t('Korrekturen')} caption={t('Versioniert: nichts wird überschrieben, alles lässt sich rückgängig machen')} />
      {edits.length === 0 ? (
        <div className="tl-card note-card">{t('Noch keine Korrekturen. Über das Menü eines Spans kannst du Zeit einem anderen Projekt zuordnen, teilen oder verwerfen.')}</div>
      ) : (
        <div className="tl-card table">
          {[...edits].reverse().map((edit) => {
            const reverted = edit.revertedAt !== null;
            const range = `${fmt.time(edit.startAt)}–${fmt.time(edit.endAt)}`;
            const title = edit.kind === 'discard' ? t('%@ verworfen', range) : t('%@ → %@', range, findProject(store.ws, edit.projectId)?.name ?? t('gelöschtes Projekt'));
            const meta = [fmt.started(edit.createdAt, now), ...(edit.createdBy ? [edit.createdBy] : []), ...(edit.note ? [edit.note] : []),
              ...(edit.revertedAt !== null ? [t('zurückgenommen %@', fmt.started(edit.revertedAt, now))] : [])].join(' · ');
            return (
              <div key={edit.id} className="table__row correction-grid">
                <Icon name={edit.kind === 'discard' ? 'trash' : 'undo'} size={16} />
                <span className="correction__text">
                  <span className={reverted ? 'correction__title is-reverted' : 'correction__title'}>{title}</span>
                  <span className="note">{meta}</span>
                </span>
                <span className={reverted ? 'table__num table__num--muted' : 'table__num'}>{fmt.duration(edit.endAt - edit.startAt)}</span>
                {reverted ? <span className="note--faint correction__action">{t('rückgängig gemacht')}</span>
                  : <Button variant="plain" size="sm" className="correction__action" onClick={() => void store.apply(m.revertCorrection(edit, store.clock()))}>{t('Rückgängig')}</Button>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function Usage({ usage, countedMs }: { usage: UsageSummary; countedMs: number }) {
  const t = useStore().t;
  const fmt = useFmt();
  const caption = usage.sources.has('otel') ? t('Aus Claude Codes OpenTelemetry') : usage.sources.has('log') ? t('Aus den Codex-Logs') : undefined;
  let note: string | null = null;
  if (usage.activeCliMs !== null) {
    const diff = usage.activeCliMs - countedMs;
    note = `${t('Claude Code meldet %@ aktive Zeit, Tellary zählt %@.', fmt.duration(usage.activeCliMs), fmt.duration(countedMs))} ${
      Math.abs(diff) < 60_000 ? t('Die Werte stimmen überein.')
        : diff > 0 ? t('Tellary zählt konservativ: lange Antworten ohne Tool-Aufruf zählen erst ab 2 Minuten ohne Event nicht mehr.')
        : t('Tellary zählt auch laufende Tools, die Claude Code nicht als aktiv meldet.')}`;
  }
  return (
    <div className="stack">
      <SectionHeader title={t('Token-Nutzung')} caption={caption} />
      <div className="tl-card usage">
        <div className="figures">
          <Figure label={t('Tokens gesamt')} value={fmt.tokens(totalTokens(usage))} strong />
          <Figure label={t('Eingabe')} value={fmt.tokens(usage.input)} />
          <Figure label={t('Cache gelesen')} value={fmt.tokens(usage.cacheRead)} />
          {usage.cacheWrite > 0 ? <Figure label={t('Cache geschrieben')} value={fmt.tokens(usage.cacheWrite)} /> : null}
          <Figure label={t('Ausgabe')} value={fmt.tokens(usage.output)} />
          {usage.costMicroUSD !== null ? <Figure label={t('Kosten laut Anbieter')} value={fmt.usd(usage.costMicroUSD)} strong /> : null}
        </div>
        {note ? <p className="note">{note}</p> : null}
      </div>
    </div>
  );
}

function Events({ events, now }: { events: RawEvent[]; now: number }) {
  const t = useStore().t;
  const fmt = useFmt();
  const [all, setAll] = useState(false);
  const shown = (all ? events : events.slice(-200)).slice().reverse();
  const seconds = (at: number) => `${fmt.started(at, now)}:${String(Math.floor(at / 1000) % 60).padStart(2, '0')}`;
  return (
    <div className="stack">
      <SectionHeader title={t('Roh-Events')} caption={t('%lld Events · nur Metadaten, keine Inhalte', events.length)} />
      {events.length === 0 ? <div className="tl-card note-card">{t('Für diese Session liegen auf dem Server keine Events vor.')}</div> : (
        <div className="tl-card events">
          <div className="table__scroll">
            <table className="events__table">
              <thead><tr><th>{t('Zeit')}</th><th>{t('Event')}</th><th>{t('Quelle')}</th><th>{t('Provider-Event')}</th><th>{t('Tool-ID')}</th><th>{t('Grund')}</th></tr></thead>
              <tbody>
                {shown.map((e) => (
                  <tr key={e.id}>
                    <td className="mono-num">{seconds(e.at)}</td><td className="mono">{e.type}</td><td>{e.source}</td>
                    <td className="mono">{e.provider_event ?? '–'}</td><td className="mono clip-cell">{e.tool_use_id ?? '–'}</td><td>{e.reason ?? '–'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {events.length > 200 && !all ? <Button variant="plain" size="sm" onClick={() => setAll(true)}>{t('Alle %lld Events zeigen', events.length)}</Button> : null}
    </div>
  );
}
